import React, { useState, useEffect } from 'react';
import SEO from './SEO';
import '../styles/Tools.css';

function Tools() {
  const [activeTab, setActiveTab] = useState('calorie');

  // 1. Calorie Calculator State
  const [age, setAge] = useState(25);
  const [gender, setGender] = useState('female');
  const [weight, setWeight] = useState(62);
  const [height, setHeight] = useState(165);
  const [activity, setActivity] = useState(1.375); // Lightly active
  const [calResult, setCalResult] = useState(null);

  const calculateCalorie = () => {
    // Mifflin-St Jeor Formula
    let bmr = 0;
    if (gender === 'male') {
      bmr = 10 * weight + 6.25 * height - 5 * age + 5;
    } else {
      bmr = 10 * weight + 6.25 * height - 5 * age - 161;
    }
    const tdee = Math.round(bmr * activity);
    setCalResult({
      bmr: Math.round(bmr),
      tdee: tdee,
      weightLoss: tdee - 500,
      weightGain: tdee + 400,
      fiberGoal: Math.round(weight * 0.5) // ~0.5g per kg weight, minimum 25g
    });
  };

  useEffect(() => {
    calculateCalorie();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [age, gender, weight, height, activity]);

  // 2. Workout Generator State
  const [workoutGoal, setWorkoutGoal] = useState('motility');
  const [equipment, setEquipment] = useState('bodyweight');
  const [level, setLevel] = useState('beginner');
  const [generatedWorkout, setGeneratedWorkout] = useState(null);

  // Timer inside Workout Generator
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [timerActive, setTimerActive] = useState(false);

  useEffect(() => {
    let interval = null;
    if (timerActive && timerSeconds > 0) {
      interval = setInterval(() => {
        setTimerSeconds((prev) => prev - 1);
      }, 1000);
    } else if (timerSeconds === 0) {
      setTimerActive(false);
    }
    return () => clearInterval(interval);
  }, [timerActive, timerSeconds]);

  const startTimer = (secs) => {
    setTimerSeconds(secs);
    setTimerActive(true);
  };

  const generateWorkout = () => {
    const workoutsDatabase = {
      motility: {
        title: "Microbiome & Motility Flow",
        description: "Focuses on gentle abdominal compression and parasympathetic stimulation to relieve bloating and promote digestive movement.",
        exercises: [
          { name: "Deep Belly Diaphragmatic Breathing", duration: "5 minutes", desc: "Activates vagus nerve to enter rest-and-digest mode.", timer: 300 },
          { name: "Cat-Cow Stretch Flow", duration: "3 minutes (3 sets)", desc: "Alternates flexion and extension of spine, massaging digestion organs.", timer: 180 },
          { name: "Supine Spinal Twist", duration: "3 minutes (each side)", desc: "Relieves trapped air and increases abdominal circulation.", timer: 180 },
          { name: "Relaxing Savasana with Heat Pack", duration: "5 minutes", desc: "Relaxes intestinal smooth muscle cells.", timer: 300 }
        ]
      },
      aerobic: {
        title: "Metabolic Aerobic Booster",
        description: "Moderate intensity cardio to accelerate gastric transit and improve insulin sensitivity.",
        exercises: [
          { name: "Light Jogging or Brisk Walking", duration: "10 minutes", desc: "Warmup targeting low-impact joint movements.", timer: 600 },
          { name: "Bodyweight High Knees", duration: "45 seconds work / 30s rest (4 rounds)", desc: "Stimulates lower abdominal muscles.", timer: 45 },
          { name: "Bicycle Crunches", duration: "12 reps per side (3 sets)", desc: "Boosts abdominal core heat and digestion transit speed.", timer: 60 },
          { name: "Active Cool Down Walk", duration: "5 minutes", desc: "Lowers heart rate and promotes normal peristalsis.", timer: 300 }
        ]
      },
      strength: {
        title: "Core & Pelvic Floor Builder",
        description: "Strengthens core and stabilizing muscles supporting gut health, posture, and pelvic organs.",
        exercises: [
          { name: "Pelvic Tilts & Glute Bridges", duration: "15 reps (3 sets)", desc: "Activates lower stabilizers and glutes.", timer: 90 },
          { name: "Bird-Dog Core Extension", duration: "10 reps per side (3 sets)", desc: "Strengthens back and core without high intra-abdominal pressure.", timer: 90 },
          { name: "Forearm Plank Hold", duration: "45 seconds (3 rounds)", desc: "Tones transverse abdominis supporting internal viscera.", timer: 45 },
          { name: "Child's Pose Rest Stretch", duration: "2 minutes", desc: "Deeply stretches lower back and abdominal wall.", timer: 120 }
        ]
      }
    };

    const selected = workoutsDatabase[workoutGoal] || workoutsDatabase.motility;
    setGeneratedWorkout(selected);
  };

  return (
    <main className="tools-page" style={{ padding: '8rem 0 4rem 0', background: 'var(--bg-color)', color: 'var(--text-primary)', minHeight: '100vh' }}>
      <SEO
        title="Free Health Utilities & Calorie Tools"
        description="Access free calculators, custom workout builders, and gut fiber macro trackers to support your wellness journey."
        canonical="/tools"
      />
      <div className="container">
        <h1 style={{ fontSize: 'clamp(2.2rem, 5vw, 3rem)', color: '#fff', textAlign: 'center', marginBottom: '0.5rem' }}>
          Free Interactive <span className="highlight">Health Utilities.</span>
        </h1>
        <p style={{ color: 'var(--text-secondary)', textAlign: 'center', maxWidth: '650px', margin: '0 auto 3rem auto', fontSize: '1.1rem' }}>
          Free, ad-free science-based tools to evaluate your metabolic requirements, design digestion routines, and track daily fiber targets.
        </p>

        {/* Tab Headers */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginBottom: '3rem', flexWrap: 'wrap' }}>
          <button
            onClick={() => setActiveTab('calorie')}
            className={`tab-btn ${activeTab === 'calorie' ? 'active' : ''}`}
          >
            📋 Calorie Calculator
          </button>
          <button
            onClick={() => setActiveTab('workout')}
            className={`tab-btn ${activeTab === 'workout' ? 'active' : ''}`}
          >
            🏋️ Workout Generator
          </button>
        </div>

        {/* TAB 1: CALORIE CALCULATOR */}
        {activeTab === 'calorie' && (
          <div className="tool-card animate-fade">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2.5rem', alignItems: 'start' }} className="grid-responsive">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
                <h3 style={{ color: '#fff', margin: '0 0 1rem 0', fontSize: '1.5rem' }}>Personal Metrics</h3>
                
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <button
                    onClick={() => setGender('female')}
                    className={`gender-btn ${gender === 'female' ? 'active' : ''}`}
                    style={{ flex: 1 }}
                  >
                    Female
                  </button>
                  <button
                    onClick={() => setGender('male')}
                    className={`gender-btn ${gender === 'male' ? 'active' : ''}`}
                    style={{ flex: 1 }}
                  >
                    Male
                  </button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.4rem' }}>Age (years)</label>
                    <input
                      type="number"
                      value={age}
                      onChange={(e) => setAge(Number(e.target.value))}
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.4rem' }}>Weight (kg)</label>
                    <input
                      type="number"
                      value={weight}
                      onChange={(e) => setWeight(Number(e.target.value))}
                      className="input-field"
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.4rem' }}>Height (cm)</label>
                  <input
                    type="number"
                    value={height}
                    onChange={(e) => setHeight(Number(e.target.value))}
                    className="input-field"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.4rem' }}>Weekly Physical Activity</label>
                  <select
                    value={activity}
                    onChange={(e) => setActivity(Number(e.target.value))}
                    className="input-field"
                  >
                    <option value={1.2}>Sedentary (No exercise, desk job)</option>
                    <option value={1.375}>Light Active (1-3 days/week gentle walking)</option>
                    <option value={1.55}>Moderately Active (3-5 days/week medium workouts)</option>
                    <option value={1.725}>Very Active (6-7 days/week intense workouts)</option>
                  </select>
                </div>
              </div>

              {calResult && (
                <div style={{ background: 'rgba(255,255,255,0.02)', padding: '2rem', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.05)' }}>
                  <h3 style={{ color: '#fff', margin: '0 0 1.5rem 0', fontSize: '1.5rem' }}>Your Calculated Output</h3>

                  <div style={{ marginBottom: '1.5rem' }}>
                    <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>BASAL METABOLIC RATE (BMR)</div>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold', color: 'var(--accent-color)' }}>{calResult.bmr} <span style={{ fontSize: '1rem', fontWeight: 'normal' }}>kcal/day</span></div>
                  </div>

                  <div style={{ marginBottom: '1.5rem' }}>
                    <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>TOTAL DAILY ENERGY EXPENDITURE (TDEE)</div>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#fff' }}>{calResult.tdee} <span style={{ fontSize: '1rem', fontWeight: 'normal' }}>kcal/day</span></div>
                  </div>

                  <hr style={{ border: '0', borderTop: '1px solid rgba(255,255,255,0.08)', margin: '1.5rem 0' }} />

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: 'var(--text-secondary)' }}>Weight Loss (-500 kcal):</span>
                      <strong style={{ color: '#63b3ed', fontSize: '1.1rem' }}>{calResult.weightLoss} kcal</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: 'var(--text-secondary)' }}>Weight Maintenance (+0 kcal):</span>
                      <strong style={{ color: '#fff', fontSize: '1.1rem' }}>{calResult.tdee} kcal</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: 'var(--text-secondary)' }}>Muscle Gain (+400 kcal):</span>
                      <strong style={{ color: '#f6ad55', fontSize: '1.1rem' }}>{calResult.weightGain} kcal</strong>
                    </div>
                  </div>

                  <div style={{ marginTop: '2rem', padding: '1rem', background: 'rgba(78,205,196,0.1)', borderRadius: '8px', borderLeft: '4px solid var(--accent-color)' }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: 'bold', color: 'var(--accent-color)', textTransform: 'uppercase' }}>🍀 Gut Health Fiber Recommendation</div>
                    <p style={{ margin: '0.4rem 0 0 0', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                      Based on your weight, target at least <strong>{Math.max(25, calResult.fiberGoal)}g</strong> of dietary fiber per day to optimize intestinal microflora growth.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* TAB 2: WORKOUT GENERATOR */}
        {activeTab === 'workout' && (
          <div className="tool-card animate-fade">
            <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: '2.5rem', alignItems: 'start' }} className="grid-responsive">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.2rem' }}>
                <h3 style={{ color: '#fff', margin: '0 0 1rem 0' }}>Configure Routine</h3>

                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.4rem' }}>Primary Goal</label>
                  <select
                    value={workoutGoal}
                    onChange={(e) => setWorkoutGoal(e.target.value)}
                    className="input-field"
                  >
                    <option value="motility">Gut Motility & Digestion</option>
                    <option value="aerobic">Aerobic Cardio Stimulator</option>
                    <option value="strength">Core & Stabilization Strength</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.4rem' }}>Equipment</label>
                  <select
                    value={equipment}
                    onChange={(e) => setEquipment(e.target.value)}
                    className="input-field"
                  >
                    <option value="bodyweight">No Equipment (Bodyweight Only)</option>
                    <option value="dumbbells">Bands / Dumbbells</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.4rem' }}>Fitness Level</label>
                  <select
                    value={level}
                    onChange={(e) => setLevel(e.target.value)}
                    className="input-field"
                  >
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                  </select>
                </div>

                <button
                  onClick={generateWorkout}
                  className="button button-primary"
                  style={{ width: '100%', padding: '0.8rem' }}
                >
                  ⚡ Generate Routine
                </button>
              </div>

              <div style={{ borderLeft: '1px solid rgba(255,255,255,0.08)', paddingLeft: '2rem' }} className="border-responsive">
                {generatedWorkout ? (
                  <div>
                    <span style={{ fontSize: '0.8rem', background: 'var(--accent-color)', color: '#000', padding: '0.2rem 0.5rem', borderRadius: '4px', fontWeight: 'bold' }}>GENERATED PLAN</span>
                    <h2 style={{ color: '#fff', marginTop: '0.5rem', marginBottom: '0.3rem' }}>{generatedWorkout.title}</h2>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', margin: '0 0 2rem 0' }}>{generatedWorkout.description}</p>

                    {/* Timer Widget */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', padding: '1.2rem', background: 'rgba(255,255,255,0.03)', borderRadius: '8px', marginBottom: '2rem', border: '1px solid rgba(255,255,255,0.05)' }}>
                      <div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>ROUTINE TIMER</div>
                        <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: 'var(--accent-color)' }}>
                          {Math.floor(timerSeconds / 60)}:{(timerSeconds % 60).toString().padStart(2, '0')}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          onClick={() => setTimerActive(!timerActive)}
                          disabled={timerSeconds === 0}
                          className="timer-control"
                          style={{ background: timerActive ? '#e53e3e' : '#319795' }}
                        >
                          {timerActive ? 'Pause' : 'Resume'}
                        </button>
                        <button
                          onClick={() => { setTimerActive(false); setTimerSeconds(0); }}
                          className="timer-control"
                          style={{ background: '#4a5568' }}
                        >
                          Clear
                        </button>
                      </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                      {generatedWorkout.exercises.map((ex, index) => (
                        <div key={index} style={{ padding: '1rem', background: 'rgba(0,0,0,0.15)', borderRadius: '8px', borderLeft: '4px solid var(--accent-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <strong style={{ color: '#fff', fontSize: '1.1rem' }}>{ex.name}</strong>
                            <p style={{ margin: '0.2rem 0 0 0', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{ex.desc}</p>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', display: 'block', marginBottom: '0.4rem' }}>{ex.duration}</span>
                            <button
                              onClick={() => startTimer(ex.timer)}
                              style={{ background: 'transparent', border: '1px solid var(--accent-color)', color: 'var(--accent-color)', fontSize: '0.75rem', padding: '0.25rem 0.6rem', borderRadius: '4px', cursor: 'pointer' }}
                            >
                              ▶ Start Timer
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: '300px', color: 'var(--text-secondary)' }}>
                    <span style={{ fontSize: '3rem' }}>🏃‍♀️</span>
                    <p style={{ marginTop: '1rem' }}>Select your goals on the left and click "Generate Routine" to load your custom exercise schedule.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

      </div>
    </main>
  );
}

export default Tools;
